/**
 * Cloud Farm detection for the TestRelic Playwright reporter.
 *
 * When Playwright tests are executed on a remote browser farm (BrowserStack
 * or LambdaTest), the reporter needs to forward `cloudPlatform`,
 * `cloudBuildId`, and `cloudSessionId` to the TestRelic server so the run
 * row can be linked to the vendor session (and, downstream, to the
 * ingested artifacts and Ask-AI tooling).
 *
 * Detection is best-effort and purely metadata-based:
 * 1. Prefer env vars the user sets in their Playwright config
 *    (TESTRELIC_CLOUD_PLATFORM, TESTRELIC_CLOUD_BUILD_ID, TESTRELIC_CLOUD_SESSION_ID).
 * 2. Fall back to vendor env vars commonly present in CI
 *    (BROWSERSTACK_USERNAME / LT_USERNAME / LAMBDATEST_USERNAME).
 * 3. As a last resort, parse the `PLAYWRIGHT_WS_ENDPOINT` env var if the
 *    project pipes its LT/BS wsEndpoint through that convention.
 *
 * The returned values are optional — the SDK simply omits them from
 * `/runs/init` when nothing is detected.
 */
export interface CloudFarmMetadata {
  readonly cloudPlatform?: 'browserstack' | 'lambdatest';
  readonly cloudBuildId?: string;
  readonly cloudSessionId?: string;
}

export function detectCloudFarm(env: NodeJS.ProcessEnv = process.env): CloudFarmMetadata {
  // 1. Explicit TestRelic env overrides — highest priority.
  const explicitPlatform = normalizePlatform(env.TESTRELIC_CLOUD_PLATFORM);
  if (explicitPlatform) {
    return {
      cloudPlatform: explicitPlatform,
      cloudBuildId: env.TESTRELIC_CLOUD_BUILD_ID || undefined,
      cloudSessionId: env.TESTRELIC_CLOUD_SESSION_ID || undefined,
    };
  }

  // 2. Vendor-native env vars.
  if (env.BROWSERSTACK_USERNAME || env.BROWSERSTACK_ACCESS_KEY) {
    return {
      cloudPlatform: 'browserstack',
      cloudBuildId: env.BROWSERSTACK_BUILD_NAME || env.BROWSERSTACK_BUILD_ID || undefined,
      cloudSessionId: env.BROWSERSTACK_SESSION_ID || undefined,
    };
  }
  if (
    env.LT_USERNAME || env.LAMBDATEST_USERNAME ||
    env.LT_ACCESS_KEY || env.LAMBDATEST_ACCESS_KEY
  ) {
    return {
      cloudPlatform: 'lambdatest',
      cloudBuildId: env.LT_BUILD || env.LAMBDATEST_BUILD || undefined,
      cloudSessionId: env.LT_SESSION_ID || env.LAMBDATEST_SESSION_ID || undefined,
    };
  }

  // 3. Parse wsEndpoint hostname (opt-in convention).
  const ws = env.PLAYWRIGHT_WS_ENDPOINT;
  if (ws) {
    if (/cdp\.lambdatest\.com/i.test(ws)) return { cloudPlatform: 'lambdatest' };
    if (/cdp\.browserstack\.com/i.test(ws)) return { cloudPlatform: 'browserstack' };
  }

  return {};
}

function normalizePlatform(raw: string | undefined): 'browserstack' | 'lambdatest' | undefined {
  if (!raw) return undefined;
  const v = raw.toLowerCase().trim();
  if (v === 'browserstack' || v === 'bs') return 'browserstack';
  if (v === 'lambdatest' || v === 'lt') return 'lambdatest';
  return undefined;
}
